import { Ability } from '@casl/ability'
import { useAbility } from '@casl/react'
import { UserPermissionFeature } from '../../api'
import { AbilityContext } from '../../api/components/Can'

export interface RebateOptimizerAccess {
  /** User has the standalone Rebate Optimizer entitlement. */
  hasRebateOptimizer: boolean
  /** Reduced Market Baskets experience: has Rebate Optimizer but no Analyze Baskets access. */
  basketsLite: boolean
  /** Reduced Contracts experience: has Rebate Optimizer but no Analyze Contracts access. */
  contractsLite: boolean
  /** Reduced Data Explorer experience: has Rebate Optimizer but no Analyze Spending access. */
  dataExplorerLite: boolean
}

/**
 * Per-surface lite/full derivation (work item #7206). Each surface is "full" iff the user has that
 * *specific* Analyze feature (or full-Analyze access); otherwise a Rebate-Optimizer user gets the
 * lite version of that surface. So e.g. Rebate Optimizer + Spending ⇒ full Data Explorer but lite
 * Baskets and Contracts. Extracted from the hook so it can be unit-tested without rendering.
 */
export function deriveRebateOptimizerAccess(ability: Ability): RebateOptimizerAccess {
  const can = (feature: UserPermissionFeature) => ability.can('access', feature)
  const hasRebateOptimizer = can(UserPermissionFeature.RebateOptimizer)
  // Full-Analyze access ("access all") grants every sub-feature, so it makes every surface full.
  const fullAnalytics = can(UserPermissionFeature.Analyze)

  const canBaskets = fullAnalytics || can(UserPermissionFeature.Baskets) || can(UserPermissionFeature.BasketsReadonly)
  const canContracts = fullAnalytics || can(UserPermissionFeature.Contracts)
  const canDataExplorer = fullAnalytics || can(UserPermissionFeature.Spending)

  return {
    hasRebateOptimizer,
    basketsLite: hasRebateOptimizer && !canBaskets,
    contractsLite: hasRebateOptimizer && !canContracts,
    dataExplorerLite: hasRebateOptimizer && !canDataExplorer,
  }
}

/** Single source of truth for per-surface lite/full gating of the decoupled Rebate Optimizer surfaces. */
export function useRebateOptimizerAccess(): RebateOptimizerAccess {
  const ability = useAbility(AbilityContext)
  return deriveRebateOptimizerAccess(ability)
}
